/**
 * What a plan row sends, in units and in value. One rule for the grid's value column, its
 * totals row, the CSV and the row sort, which each used to work the quantity out for themselves.
 */

interface SendSlot {
    transferId: string | null;
    cellState: string;
    approvedQty: number | null;
    recommendedQty: number;
}

interface SendRow {
    productId: string;
    toWarehouseId: string | null;
    /**
     * The order's line for this SKU, before tax, read live from the plan's order; each absent
     * or null when the order does not state it.
     */
    orderedValue?: number | null;
    orderedQty?: number | null;
    unitPrice?: number | null;
    fromWhSlots: Record<string, SendSlot>;
}

const NO_EDITS: ReadonlyMap<string, number> = new Map();

/**
 * Selection / edit key for one SKU × From-WH slot. Persisted slots are keyed by transfer id;
 * available slots (no transfer yet) by the same `new::` key the inline cell editor writes to, so a
 * bulk action can plan an available row exactly as if the operator had typed into its cell.
 */
export const slotEditKey = (row: SendRow, fromWhId: string): string =>
    row.fromWhSlots[fromWhId]?.transferId ??
    `new::${row.productId}::${row.toWarehouseId ?? 'agg'}::${fromWhId}`;

/**
 * Units a row sends across its source WHs: a quantity typed into a cell wins, else a planned
 * slot's approved or recommended quantity; excluded and unplanned slots count as 0.
 */
export const rowSendQty = (row: SendRow, editQtyMap: ReadonlyMap<string, number> = NO_EDITS): number =>
    Object.entries(row.fromWhSlots).reduce(
        (sum, [fromWhId, slot]) =>
            sum +
            (editQtyMap.get(slotEditKey(row, fromWhId)) ??
                (slot.cellState === 'recommended' ? (slot.approvedQty ?? slot.recommendedQty) : 0)),
        0,
    );

/**
 * What a quantity of the row's SKU is worth on the order; null when the order does not price it.
 *
 * The order's line value is scaled by the share of the ordered quantity, rather than the
 * quantity multiplied by the unit price: a partner prints its unit price rounded (Instamart's
 * 4270.76 for a line worth 213537.97 over 50 units), so multiplying it back up drifts from the
 * line value by a few paise. Scaling makes sending what was ordered worth exactly what was
 * ordered. The unit price is the fallback for a line with no value or no ordered quantity.
 */
export const valueOfQty = (row: SendRow, qty: number): number | null => {
    const value =
        row.orderedValue != null && row.orderedQty != null && row.orderedQty > 0
            ? (row.orderedValue * qty) / row.orderedQty
            : row.unitPrice != null
              ? row.unitPrice * qty
              : null;
    return value === null ? null : Math.round(value * 100) / 100;
};

/** What the row's send quantity is worth on the order; null when the SKU is not priced. */
export const rowSendingValue = (row: SendRow, editQtyMap?: ReadonlyMap<string, number>): number | null =>
    valueOfQty(row, rowSendQty(row, editQtyMap));
